import React, { useState } from 'react';
import AnalysisPageLayout from '../../components/aesthetic/AnalysisPageLayout';
import ImageUpload, { validateImageFile } from '../../components/aesthetic/ImageUpload';
import MetricCard from '../../components/aesthetic/MetricCard';
import BeforeAfterComparison from '../../components/aesthetic/BeforeAfterComparison';
import SymmetryMetricsTable from '../../components/aesthetic/SymmetryMetricsTable';
import LoadingOverlay from '../../components/aesthetic/LoadingOverlay';
import ErrorBanner from '../../components/aesthetic/ErrorBanner';
import EmptyState from '../../components/aesthetic/EmptyState';
import { analyzeJawline } from '../../services/aestheticApi';
import { readFileAsDataUrl, toDataUri } from '../../utils/imageUtils';

function JawlineAnalysis() {
  const [beforeImage, setBeforeImage] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [beforePreview, setBeforePreview] = useState(null);
  const [afterPreview, setAfterPreview] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleImageChange = async (field, file) => {
    setFieldErrors((prev) => ({ ...prev, [field]: null }));
    setError('');

    if (!file) {
      if (field === 'before') {
        setBeforeImage(null);
        setBeforePreview(null);
      } else {
        setAfterImage(null);
        setAfterPreview(null);
      }
      return;
    }

    const validationError = validateImageFile(file);
    if (validationError) {
      setFieldErrors((prev) => ({ ...prev, [field]: validationError }));
      return;
    }

    const preview = await readFileAsDataUrl(file);
    if (field === 'before') {
      setBeforeImage(file);
      setBeforePreview(preview);
    } else {
      setAfterImage(file);
      setAfterPreview(preview);
    }
  };

  const handleAnalyze = async () => {
    const errors = {};
    if (!beforeImage) errors.before = 'Before image is required.';
    if (!afterImage) errors.after = 'After image is required.';

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setError('Please upload both before and after images.');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('before_image', beforeImage);
      formData.append('after_image', afterImage);

      const data = await analyzeJawline(formData);

      if (!data?.success) {
        throw new Error(data?.message || 'Analysis failed. Please try again.');
      }

      setResults(data);
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Unable to analyze images.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const overlayImage = results ? toDataUri(results.overlayImage) : null;
  const heatmapImage = results ? toDataUri(results.heatmapImage) : null;
  const symmetryMetrics = results?.aiBeautyScore?.symmetryMetrics;
  const overallHarmony = results?.facialHarmonyIndexAfter ?? results?.facialHarmonyIndexBefore;

  return (
    <AnalysisPageLayout
      title="Jawline Analysis"
      subtitle="Evaluate facial harmony and jawline symmetry improvements from before and after images."
    >
      {loading && <LoadingOverlay message="Running facial harmony analysis…" />}

      <section className="aesthetic-section">
        <h2 className="aesthetic-section-title">Upload Images</h2>
        <ErrorBanner message={error} />
        <div className="upload-form-grid">
          <ImageUpload
            label="Before Image"
            preview={beforePreview}
            onChange={(file) => handleImageChange('before', file)}
            error={fieldErrors.before}
          />
          <ImageUpload
            label="After Image"
            preview={afterPreview}
            onChange={(file) => handleImageChange('after', file)}
            error={fieldErrors.after}
          />
        </div>
        <div className="upload-form-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleAnalyze}
            disabled={loading}
          >
            Analyze
          </button>
        </div>
      </section>

      {!results && !loading && (
        <section className="aesthetic-section">
          <EmptyState
            title="No analysis results yet"
            description="Upload before and after facial images, then click Analyze to view harmony index and jawline symmetry metrics."
          />
        </section>
      )}

      {results && (
        <>
          <section className="aesthetic-section">
            <h2 className="aesthetic-section-title">Analytics Dashboard</h2>
            <div className="metrics-grid">
              <MetricCard label="Harmony Index Before" value={results.facialHarmonyIndexBefore} />
              <MetricCard label="Harmony Index After" value={results.facialHarmonyIndexAfter} highlight />
              <MetricCard label="Harmony Improvement" value={results.harmonyImprovement} unit="pts" highlight />
              <MetricCard label="Overall Harmony Score" value={overallHarmony} highlight />
            </div>
          </section>

          {symmetryMetrics && (
            <SymmetryMetricsTable symmetryMetrics={symmetryMetrics} />
          )}

          {(overlayImage || heatmapImage) && (
            <section className="aesthetic-section">
              <h2 className="aesthetic-section-title">Visualization</h2>
              <div className="visualization-grid">
                {overlayImage && (
                  <div className="visualization-card">
                    <div className="visualization-card-header">Overlay Image</div>
                    <div className="visualization-card-body">
                      <img src={overlayImage} alt="Jawline overlay analysis" />
                    </div>
                  </div>
                )}
                {heatmapImage && (
                  <div className="visualization-card">
                    <div className="visualization-card-header">Heatmap Image</div>
                    <div className="visualization-card-body">
                      <img src={heatmapImage} alt="Jawline heatmap analysis" />
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          <section className="aesthetic-section">
            <h2 className="aesthetic-section-title">Before / After Comparison</h2>
            <BeforeAfterComparison
              beforeImage={beforePreview}
              afterImage={afterPreview}
            />
          </section>
        </>
      )}
    </AnalysisPageLayout>
  );
}

export default JawlineAnalysis;
